import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { faBoxOpen, faChevronRight, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { ColumnModel, GridComponent, PageSettingsModel, RowDataBoundEventArgs, RowSelectEventArgs } from '@syncfusion/ej2-angular-grids';
import { finalize } from 'rxjs/operators';
import { ErrorService } from '../../../../@core/utils/error.service';
import { InventoryPackageTypeDTO } from '../../../modules/inventory/models/inventory-package-type';
import { InventoryService } from '../../../modules/pick-plan/services/inventory.service';
import { InventoryPackageTypeEditComponent } from './inventory-package-type-edit/inventory-package-type-edit.component';

const MOBILE_PAGE_SIZE = 25;

export type PackageTypeStatusFilter = 'all' | 'active' | 'inactive';

export interface PackageTypeRow extends InventoryPackageTypeDTO {
  weightLabel: string;
  dimensions: string;
  componentLabel: string;
  statusLabel: string;
}

@Component({
  selector: 'package-types',
  templateUrl: './package-types.component.html',
  styleUrls: ['./package-types.component.scss']
})
export class PackageTypesComponent implements OnInit {
  @ViewChild('grid') public grid: GridComponent;
  @ViewChild('nameTemplate', { static: true }) public nameTemplate: any;
  @ViewChild('weightTemplate', { static: true }) public weightTemplate: any;
  @ViewChild('dimensionsTemplate', { static: true }) public dimensionsTemplate: any;
  @ViewChild('componentTemplate', { static: true }) public componentTemplate: any;
  @ViewChild('statusTemplate', { static: true }) public statusTemplate: any;
  @ViewChild('actionTemplate', { static: true }) public actionTemplate: any;

  private destroyRef = inject(DestroyRef);

  public faBoxOpen = faBoxOpen;
  public faChevronRight = faChevronRight;
  public faPlus = faPlus;
  public faSearch = faSearch;

  public isLoading = true;
  public rows: PackageTypeRow[] = [];
  private allRows: PackageTypeRow[] = [];
  public mobileVisibleCount = MOBILE_PAGE_SIZE;

  public searchTerm = '';
  public statusFilter: PackageTypeStatusFilter = 'all';
  public counts: Record<PackageTypeStatusFilter, number> = { all: 0, active: 0, inactive: 0 };
  public readonly statusChips: { key: PackageTypeStatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'inactive', label: 'Inactive' }
  ];

  public columns: ColumnModel[] = [];
  public pageSettings: PageSettingsModel = { pageSize: 50, pageSizes: [50, 100, 200] };

  constructor(
    private dialog: MatDialog,
    private errorService: ErrorService,
    private packageTypeService: InventoryService
  ) { }

  ngOnInit() {
    this.columns = [
      { field: 'name', headerText: 'Name', width: 260, template: this.nameTemplate },
      { field: 'unitOfMeasure', headerText: 'Unit', width: 110 },
      { field: 'quantityPerPallet', headerText: 'Per Pallet', width: 120, format: 'N0', textAlign: 'Right', headerTextAlign: 'Right' },
      { field: 'packageWeight', headerText: 'Weight', width: 120, textAlign: 'Right', headerTextAlign: 'Right', template: this.weightTemplate },
      { field: 'dimensions', headerText: 'Dimensions (H × W × L)', width: 200, template: this.dimensionsTemplate },
      { field: 'componentLabel', headerText: 'Components', width: 220, template: this.componentTemplate },
      { field: 'statusLabel', headerText: 'Status', width: 120, template: this.statusTemplate },
      { headerText: '', width: 70, template: this.actionTemplate, allowSorting: false }
    ];
    this.load();
  }

  public load() {
    this.isLoading = true;
    this.packageTypeService.getInventoryPackageTypes()
      .pipe(finalize(() => this.isLoading = false), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.allRows = (data ?? []).map(x => this.toRow(x)).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
          this.counts = {
            all: this.allRows.length,
            active: this.allRows.filter(r => r.isActive).length,
            inactive: this.allRows.filter(r => !r.isActive).length
          };
          this.applyFilters();
        },
        error: err => this.errorService.handleError(err)
      });
  }

  public setStatusFilter(filter: PackageTypeStatusFilter) {
    this.statusFilter = filter;
    this.applyFilters();
  }

  public applyFilters() {
    const term = this.searchTerm.trim().toLowerCase();
    this.rows = this.allRows.filter(r =>
      (this.statusFilter === 'all' || (this.statusFilter === 'active') === !!r.isActive)
      && (!term || [r.name, r.description].some(v => v?.toLowerCase().includes(term))));
    this.mobileVisibleCount = MOBILE_PAGE_SIZE;
  }

  public onRowSelected(args: RowSelectEventArgs) {
    const row = args?.data as PackageTypeRow;
    if (row) this.openEdit(row);
  }

  public onRowDataBound(args: RowDataBoundEventArgs) {
    if (!(args.data as PackageTypeRow).isActive) {
      args.row?.classList.add('is-inactive');
    }
  }

  public createPackageType() {
    this.openEdit({});
  }

  public openEdit(packageType: InventoryPackageTypeDTO) {
    InventoryPackageTypeEditComponent.open(this.dialog, { packageType })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(saved => {
        this.grid?.clearSelection();
        if (saved) this.load();
      });
  }

  public showMoreMobile() {
    this.mobileVisibleCount += MOBILE_PAGE_SIZE;
  }

  public trackByRow(_: number, row: PackageTypeRow): number | undefined {
    return row.id;
  }

  private toRow(x: InventoryPackageTypeDTO): PackageTypeRow {
    const unit = (value: number, uom: string) => `${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}${uom ? ' ' + uom : ''}`;
    const hasDimensions = [x.height, x.width, x.length].some(v => v != null);
    const dim = (v: number) => v?.toLocaleString('en-US', { maximumFractionDigits: 2 }) ?? '–';

    return {
      ...x,
      weightLabel: x.packageWeight != null ? unit(x.packageWeight, x.weightUnitOfMeasure) : '',
      dimensions: hasDimensions
        ? `${dim(x.height)} × ${dim(x.width)} × ${dim(x.length)}${x.dimensionUnitOfMeasure ? ' ' + x.dimensionUnitOfMeasure : ''}`
        : '',
      componentLabel: x.hasComponents ? this.componentLabel(x) : '',
      statusLabel: x.isActive ? 'Active' : 'Inactive'
    };
  }

  // e.g. "4 × 11 lb Foil Bag"; any missing part is left out.
  private componentLabel(x: InventoryPackageTypeDTO): string {
    const weight = x.componentWeight != null ? `${x.componentWeight} ${x.componentWeightUnitOfMeasure ?? ''}`.trim() : '';
    const parts = [weight, x.componentName || x.componentUnitOfMeasure].filter(p => !!p).join(' ');
    return x.componentQuantityPerPackage ? `${x.componentQuantityPerPackage} × ${parts}`.trim() : parts || 'Yes';
  }
}
